import type { UserProfile, WeeklyWorkoutPreferences, WorkoutPreferenceType } from '@shared/types';
import apiClient from '../services/api';
import { logger } from './logger';

const USER_PROFILE_KEY = 'pacepilot_user_profile';

/**
 * Default user profile values
 */
export const DEFAULT_USER_PROFILE: UserProfile = {
  userId: 'default_user',
  intervalsConnected: false,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  thresholdPaceSec: 240, // 4:00 min/km
  weeklyMileage: 50,
  weeklyWorkoutPreferences: {
    Monday: ['base run', 'rest'],
    Tuesday: ['hard run', 'base run', 'rest'],
    Wednesday: ['base run', 'rest'],
    Thursday: ['base run', 'rest'],
    Friday: ['rest'],
    Saturday: ['long run', 'base run'],
    Sunday: ['rest'],
  },
  fatigueLevel: 'normal',
  ATL: 0,
  CTL: 0,
  HRV: 0,
};

/**
 * Migrate a single workout preference value from old to new format
 * Handles both legacy string and new array formats
 */
const migratePreferenceValue = (
  value: string | WorkoutPreferenceType[] | undefined,
  defaultVal: WorkoutPreferenceType[]
): WorkoutPreferenceType[] => {
  // Helper to migrate legacy values (uses string comparison for runtime safety)
  const migrateLegacy = (v: string): WorkoutPreferenceType => {
    if (v === 'run') return 'base run';
    if (v === 'ride') return 'base ride';
    return v as WorkoutPreferenceType;
  };

  // Already an array - just migrate individual values
  if (Array.isArray(value)) {
    return value.map(v => migrateLegacy(v));
  }
  // Legacy single string value
  if (typeof value === 'string') {
    return [migrateLegacy(value)];
  }
  // Undefined or invalid - use default
  return defaultVal;
};

/**
 * Load user profile from localStorage (used as cache)
 */
const loadFromLocalStorage = (): UserProfile | null => {
  try {
    const stored = localStorage.getItem(USER_PROFILE_KEY);
    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored) as Partial<UserProfile>;

    // Merge with defaults to ensure all required fields are present
    // Also migrate old 'run'/'ride' values to 'base run'/'base ride'
    // and convert legacy single-value format to array format
    const defaultPrefs = DEFAULT_USER_PROFILE.weeklyWorkoutPreferences;
    const mergedPreferences: WeeklyWorkoutPreferences = {
      Monday: migratePreferenceValue(parsed.weeklyWorkoutPreferences?.Monday, defaultPrefs.Monday),
      Tuesday: migratePreferenceValue(
        parsed.weeklyWorkoutPreferences?.Tuesday,
        defaultPrefs.Tuesday
      ),
      Wednesday: migratePreferenceValue(
        parsed.weeklyWorkoutPreferences?.Wednesday,
        defaultPrefs.Wednesday
      ),
      Thursday: migratePreferenceValue(
        parsed.weeklyWorkoutPreferences?.Thursday,
        defaultPrefs.Thursday
      ),
      Friday: migratePreferenceValue(parsed.weeklyWorkoutPreferences?.Friday, defaultPrefs.Friday),
      Saturday: migratePreferenceValue(
        parsed.weeklyWorkoutPreferences?.Saturday,
        defaultPrefs.Saturday
      ),
      Sunday: migratePreferenceValue(parsed.weeklyWorkoutPreferences?.Sunday, defaultPrefs.Sunday),
    };

    return {
      ...DEFAULT_USER_PROFILE,
      ...parsed,
      // Ensure weeklyWorkoutPreferences has all days
      weeklyWorkoutPreferences: mergedPreferences,
    };
  } catch (error) {
    logger.error('Error loading user profile from localStorage', error);
    return null;
  }
};

/**
 * Save user profile to localStorage (cache)
 */
const saveToLocalStorage = (profile: UserProfile): void => {
  try {
    localStorage.setItem(USER_PROFILE_KEY, JSON.stringify(profile));
  } catch (error) {
    logger.error('Error saving user profile to localStorage', error);
  }
};

/**
 * Load user profile - syncs from backend and uses localStorage as cache
 */
export const loadUserProfile = async (): Promise<UserProfile> => {
  try {
    // Try to load from backend first
    logger.profile('Loading from backend');
    const response = await apiClient.getUserProfile();

    if (response.success && response.data) {
      logger.profile('Successfully loaded from backend');
      // Update localStorage cache
      saveToLocalStorage(response.data);
      return response.data;
    }
  } catch (error) {
    logger.warn('Could not load profile from backend, using localStorage cache', { error });
  }

  // Fallback to localStorage if backend is unavailable
  const cachedProfile = loadFromLocalStorage();
  if (cachedProfile) {
    logger.profile('Using cached profile from localStorage');
    return cachedProfile;
  }

  logger.profile('No profile found, using default profile');
  return { ...DEFAULT_USER_PROFILE };
};

/**
 * Load user profile synchronously from localStorage only (for backwards compatibility)
 */
export const loadUserProfileSync = (): UserProfile => {
  const cached = loadFromLocalStorage();
  return cached || { ...DEFAULT_USER_PROFILE };
};

/**
 * Migrate old workout preference values to new format
 * 'run' -> 'base run', 'ride' -> 'base ride'
 */
const migrateWorkoutPreferences = (profile: UserProfile): UserProfile => {
  if (!profile.weeklyWorkoutPreferences) return profile;

  const migrated = { ...profile };
  const prefs = { ...profile.weeklyWorkoutPreferences };

  for (const day of Object.keys(prefs) as Array<keyof typeof prefs>) {
    if (prefs[day] === ('run' as any)) {
      (prefs[day] as any) = 'base run';
    } else if (prefs[day] === ('ride' as any)) {
      (prefs[day] as any) = 'base ride';
    }
  }

  migrated.weeklyWorkoutPreferences = prefs;
  return migrated;
};

/**
 * Save user profile - syncs to backend and localStorage
 */
export const saveUserProfile = async (profile: UserProfile): Promise<void> => {
  // Migrate old preference values before saving
  const migratedProfile = migrateWorkoutPreferences(profile);

  try {
    // Save to localStorage immediately (optimistic update)
    saveToLocalStorage(migratedProfile);

    // Sync to backend
    logger.profile('Syncing to backend');
    const response = await apiClient.updateUserProfile(migratedProfile);

    if (response.success) {
      logger.profile('Successfully synced to backend');
    } else {
      logger.warn('Failed to sync profile to backend', { error: response.error });
    }
  } catch (error) {
    logger.error('Error syncing user profile to backend', { error });
    // Profile is still saved to localStorage, so it's not a complete failure
  }
};

/**
 * Clear user profile from localStorage
 */
export const clearUserProfile = (): void => {
  try {
    localStorage.removeItem(USER_PROFILE_KEY);
  } catch (error) {
    logger.error('Error clearing user profile from localStorage', error);
  }
};

/**
 * Check if user profile exists in localStorage
 */
export const hasUserProfile = (): boolean => {
  try {
    return localStorage.getItem(USER_PROFILE_KEY) !== null;
  } catch (error) {
    logger.error('Error checking user profile in localStorage', error);
    return false;
  }
};
